/**
 * Document Header Component.
 *
 * Renders a professional enterprise-grade header for business documents
 * (Purchase Orders, Sales Orders, Quotes, Work Orders). Displays the
 * organization's company info, the document type and number, key dates,
 * status, and the counterparty (vendor, customer, etc.).
 *
 * Used consistently across all business form detail pages to ensure a
 * uniform, polished presentation. Also used by print templates.
 */

import { useOrgConfig } from "@/features/settings/api";
import Badge from "@/components/ui/Badge";

interface PartyInfo {
  name: string;
  contact?: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface DocumentHeaderProps {
  /** The type of document (displayed as the formal title). */
  documentType:
    | "Purchase Order"
    | "Sales Order"
    | "Invoice"
    | "Quote"
    | "Work Order";
  /** The document number (e.g., PO-000042, SO-000015). */
  documentNumber: string;
  /** Current status. */
  status: string;
  /** Status badge variant. */
  statusVariant?: "default" | "success" | "warning" | "danger" | "info";
  /** Primary date (order date, quote date, etc.). */
  date?: string | null;
  /** Date label override (default: "Date"). */
  dateLabel?: string;
  /** Due date or valid-until date. */
  dueDate?: string | null;
  /** Due date label (default: "Due"). */
  dueDateLabel?: string;
  /** Counterparty info (vendor, customer, etc.). */
  party?: PartyInfo;
  /** Label for the counterparty block (default derived from documentType). */
  partyLabel?: string;
  /** Additional metadata lines shown in the document info column. */
  extra?: Record<string, string | null | undefined>;
  /** Override address from a specific business location. */
  locationAddress?: {
    name?: string;
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    email?: string;
  };
}

function DocumentHeader({
  documentType,
  documentNumber,
  status,
  statusVariant = "default",
  date,
  dateLabel = "Date",
  dueDate,
  dueDateLabel = "Due",
  party,
  partyLabel,
  extra,
  locationAddress,
}: DocumentHeaderProps) {
  const { data: config } = useOrgConfig();
  const biz = config?.business;

  // Use location-specific address when provided, otherwise fall back to primary.
  const addr = locationAddress
    ? {
        street1: locationAddress.street1,
        street2: locationAddress.street2,
        city: locationAddress.city,
        state: locationAddress.state,
        zip: locationAddress.zip,
      }
    : biz?.primary_address;
  const contactPhone = locationAddress?.phone || biz?.phone;
  const contactEmail = locationAddress?.email || biz?.email;

  const defaultPartyLabel =
    documentType === "Purchase Order"
      ? "Vendor"
      : documentType === "Work Order"
        ? "Assigned To"
        : "Customer";

  const fmtDate = (d: string | null | undefined) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-base)] mb-6 overflow-hidden print:border-0 print:mb-4">
      {/* Top bar — document type accent */}
      <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-400 to-accent-400" />

      <div className="p-5">
        {/* Row 1: Company info + Document info */}
        <div className="flex justify-between gap-6">
          {/* Company block */}
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-[var(--text-primary)]">
              {biz?.company_name || "Your Company"}
            </h2>
            {locationAddress?.name && (
              <p className="text-xs font-medium text-[var(--text-secondary)] mt-0.5">
                {locationAddress.name}
              </p>
            )}
            {addr?.street1 && (
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {addr.street1}
                {addr.street2 ? `, ${addr.street2}` : ""}
              </p>
            )}
            {addr?.city && (
              <p className="text-xs text-[var(--text-secondary)]">
                {addr.city}, {addr.state} {addr.zip}
              </p>
            )}
            <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-tertiary)]">
              {contactPhone && <span>{contactPhone}</span>}
              {contactEmail && <span>{contactEmail}</span>}
              {biz?.website && <span>{biz.website}</span>}
            </div>
          </div>

          {/* Document info block */}
          <div className="text-right shrink-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--text-tertiary)]">
              {documentType}
            </p>
            <p className="text-xl font-bold font-mono text-brand-600 mt-0.5">
              {documentNumber}
            </p>
            <div className="mt-2 space-y-0.5 text-xs text-[var(--text-secondary)]">
              {fmtDate(date) && (
                <p>
                  {dateLabel}: {fmtDate(date)}
                </p>
              )}
              {fmtDate(dueDate) && (
                <p>
                  {dueDateLabel}: {fmtDate(dueDate)}
                </p>
              )}
              <div className="mt-1">
                <Badge variant={statusVariant}>{status}</Badge>
              </div>
              {extra &&
                Object.entries(extra).map(
                  ([k, v]) =>
                    v && (
                      <p key={k}>
                        {k}: {v}
                      </p>
                    ),
                )}
            </div>
          </div>
        </div>

        {/* Row 2: Counterparty */}
        {party?.name && (
          <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--text-tertiary)] mb-1">
              {partyLabel || defaultPartyLabel}
            </p>
            <p className="text-sm font-semibold text-[var(--text-primary)]">
              {party.name}
            </p>
            {party.contact && (
              <p className="text-xs text-[var(--text-secondary)]">
                {party.contact}
              </p>
            )}
            <div className="flex items-center gap-3 text-xs text-[var(--text-tertiary)] mt-0.5">
              {party.email && <span>{party.email}</span>}
              {party.phone && <span>{party.phone}</span>}
            </div>
            {party.address && (
              <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
                {party.address}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentHeader;
